import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  input,
} from '@angular/core';
import { InputDirective } from '@fc-shared/ui/input/core/input.directive';
import { PrefixDirective } from '@fc-shared/ui/input/core/prefix.directive';
import { SuffixDirective } from '@fc-shared/ui/input/core/suffix.directive';
import { LabelComponent } from '@fc-shared/ui/input/core/label/label.component';
import { ErrorComponent } from '@fc-core/form-utils/form-error/error.component';
import { HintComponent } from '@fc-shared/ui/input/core/hint/hint.component';
import {
  resolveErrorMessage,
  SignalFormErrorMessages,
} from '@fc-core/form-utils/signal-form-errors';
import { FORM_FIELD_CONTEXT, FormFieldContext } from './form-field-context';

@Component({
  selector: 'fc-form-field',
  imports: [ErrorComponent],
  providers: [{ provide: FORM_FIELD_CONTEXT, useExisting: FormFieldComponent }],
  template: `
    @if (label()) {
      <label class="fc-form-field-label" [attr.for]="input()?.id() ?? null">
        <ng-content select="fc-label" />
      </label>
    }
    <div class="fc-form-field-input">
      <ng-content select="[fcInput]" />
      @if (isErrorShown()) {
        <div class="error-level" [attr.id]="errorId()">
          <ng-content select="fc-error" />
          @if (!error()) {
            <fc-error>{{ autoErrorMessage() }}</fc-error>
          }
        </div>
      } @else if (hint()) {
        <div class="hints-level" [attr.id]="hintId()">
          <ng-content select="fc-hint" />
        </div>
      }
      @if (prefix()) {
        <div class="fc-form-field-prefix">
          <ng-content select="[fcPrefix]" />
        </div>
      }
      @if (suffix()) {
        <div class="fc-form-field-suffix">
          <ng-content select="[fcSuffix]" />
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.fc-form-field--has-prefix]': '!!prefix()',
    '[class.fc-form-field--has-suffix]': '!!suffix()',
    '[class.fc-form-field--show-error]': 'isErrorShown()',
    '[class.fc-form-field--required]': 'input()?.isRequired() ?? false',
  },
})
export class FormFieldComponent implements FormFieldContext {
  readonly errorMessages = input<SignalFormErrorMessages>({});

  protected readonly label = contentChild(LabelComponent);
  protected readonly input = contentChild(InputDirective);
  protected readonly error = contentChild(ErrorComponent);
  protected readonly prefix = contentChild(PrefixDirective);
  protected readonly suffix = contentChild(SuffixDirective);
  protected readonly hint = contentChild(HintComponent);

  protected readonly autoErrorMessage = computed(() =>
    resolveErrorMessage(
      this.input()?.signalFormErrors()[0],
      this.errorMessages(),
    ),
  );

  readonly isErrorShown = computed(() => {
    const control = this.input();
    const isControlInError = control?.hasFormControl()
      ? control.isInvalidAndTouched()
      : true;
    return isControlInError && (!!this.error() || !!this.autoErrorMessage());
  });

  protected readonly errorId = computed(() => this.buildDescriptionId('error'));
  protected readonly hintId = computed(() => this.buildDescriptionId('hint'));

  readonly describedByIds = computed(() => {
    if (this.isErrorShown()) return this.errorId();
    return this.hint() ? this.hintId() : null;
  });

  private buildDescriptionId(suffix: string): string | null {
    const inputId = this.input()?.id();
    return inputId ? `${inputId}-${suffix}` : null;
  }
}
